import { useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { Box, Skeleton } from "@salestrak/ui";
import { PageHeader, PageHeaderTitle, SettingsBackBar, SettingsBackBarClearance } from "#app/components/page-header";
import { SettingsPageSkeleton } from "#app/components/settings/settings-page-skeleton";
import {
  useWebhooks,
  useWebhookMutations,
} from "#app/hooks/settings/use-webhook-mutations";
import { WebhookDetailView } from "#app/components/settings/webhook-detail-view";
import { TrashConfirmDialog } from "#app/components/settings/trash-confirm-dialog";
import { useAppNavigate } from "#app/lib/hooks/use-app-navigate";
import { withPermission } from "#app/components/permissions/require-permission";

function WebhookDetailPage() {
  const { webhookId } = Route.useParams();
  const { push, replace } = useAppNavigate();
  const { webhooks, isLoading } = useWebhooks();
  const { deleteWebhook, toggleWebhookArchived } = useWebhookMutations();

  const [showDelete, setShowDelete] = useState(false);

  const webhook = webhooks.find((w) => w.webhookId === webhookId);

  if (isLoading && !webhook) {
    return (
      <Box direction="column" fill="flex" position="relative">
        {/* Real chrome: back bar + header geometry hold through the resolve;
          * only the webhook name is a bar. */}
        <SettingsBackBar label="API" onClick={() => replace("/settings/api")} />
        <Box fill="flex" overflow="auto">
          <SettingsBackBarClearance />
          <PageHeader centered pinnedBack>
            <PageHeaderTitle>
              <Skeleton delayed height={20} width={180} />
            </PageHeaderTitle>
          </PageHeader>
          <SettingsPageSkeleton groups={[{ rows: 3, unlabeled: true }]} />
        </Box>
      </Box>
    );
  }

  if (!webhook) {
    replace("/settings/api");
    return null;
  }

  async function handleDelete() {
    await deleteWebhook(webhookId);
    replace("/settings/api");
  }

  return (
    <>
      <WebhookDetailView
        webhook={webhook}
        onBack={() => replace("/settings/api")}
        onEdit={() =>
          push(`/settings/api/webhooks/${webhookId}/edit`)
        }
        onToggleArchived={() => toggleWebhookArchived(webhook)}
        onDelete={() => setShowDelete(true)}
      />

      <TrashConfirmDialog
        open={showDelete}
        onOpenChange={(open) => { if (!open) setShowDelete(false); }}
        title="Delete webhook?"
        description={`This will permanently delete the webhook for ${webhook.url}. This action cannot be undone.`}
        confirmLabel="Delete"
        onConfirm={handleDelete}
      />
    </>
  );
}

export const Route = createFileRoute(
  "/(app)/$workspace/settings/api/webhooks/$webhookId/",
)({ component: withPermission("workspace.integrations", WebhookDetailPage) });
